import React, { useCallback, useEffect, useRef } from 'react'
import { Button, Typography } from '@mui/material'

const padStyle = {
  border: '1px solid #c7c7c7',
  borderRadius: 8,
  backgroundColor: '#fff',
  width: '100%',
  maxWidth: 720,
  touchAction: 'none',
}

const getCanvasPoint = (canvas, clientX, clientY) => {
  const rect = canvas.getBoundingClientRect()
  return {
    x: ((clientX - rect.left) / rect.width) * canvas.width,
    y: ((clientY - rect.top) / rect.height) * canvas.height,
  }
}

const SignaturePad = ({
  value,
  onChange,
  height = 220,
  onSignedAtChange,
  subtitle = 'Signature of Treating Healthcare Professional (sign with finger/stylus/mouse)',
}) => {
  const canvasRef = useRef(null)
  const signatureImageRef = useRef('')
  const hasInkRef = useRef(false)
  const isDrawingRef = useRef(false)

  const commitSignature = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let dataUrl = ''
    if (hasInkRef.current) {
      const exportCanvas = document.createElement('canvas')
      const exportWidth = 560
      const exportHeight = 180
      exportCanvas.width = exportWidth
      exportCanvas.height = exportHeight

      const exportCtx = exportCanvas.getContext('2d')
      if (exportCtx) {
        exportCtx.fillStyle = '#ffffff'
        exportCtx.fillRect(0, 0, exportWidth, exportHeight)
        exportCtx.drawImage(canvas, 0, 0, exportWidth, exportHeight)
        dataUrl = exportCanvas.toDataURL('image/jpeg', 0.62)
      }
    }

    signatureImageRef.current = dataUrl
    onChange(dataUrl)
    if (onSignedAtChange) {
      onSignedAtChange(dataUrl ? new Date().toISOString() : '')
    }
  }, [onChange, onSignedAtChange])

  const drawImageToCanvas = useCallback((imageDataUrl) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    if (!imageDataUrl) {
      signatureImageRef.current = ''
      hasInkRef.current = false
      return
    }

    const img = new Image()
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
      signatureImageRef.current = imageDataUrl
      hasInkRef.current = true
    }
    img.src = imageDataUrl
  }, [])

  const configureCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const dpr = Math.max(window.devicePixelRatio || 1, 1)
    const parentWidth = canvas.parentElement?.clientWidth || 620
    const desiredWidth = Math.min(parentWidth, 720)

    canvas.style.width = `${desiredWidth}px`
    canvas.style.height = `${height}px`
    canvas.width = Math.floor(desiredWidth * dpr)
    canvas.height = Math.floor(height * dpr)

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#1d1d1d'
    ctx.lineWidth = 2.2 * dpr
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    drawImageToCanvas(signatureImageRef.current || value || '')
  }, [drawImageToCanvas, height, value])

  useEffect(() => {
    configureCanvas()
    window.addEventListener('resize', configureCanvas)

    const handleGlobalPointerUp = () => {
      if (!isDrawingRef.current) return
      isDrawingRef.current = false
      commitSignature()
    }

    window.addEventListener('pointerup', handleGlobalPointerUp)
    window.addEventListener('pointercancel', handleGlobalPointerUp)

    return () => {
      window.removeEventListener('resize', configureCanvas)
      window.removeEventListener('pointerup', handleGlobalPointerUp)
      window.removeEventListener('pointercancel', handleGlobalPointerUp)
      if (isDrawingRef.current) {
        isDrawingRef.current = false
        commitSignature()
      }
    }
  }, [configureCanvas, commitSignature])

  useEffect(() => {
    signatureImageRef.current = value || ''
    drawImageToCanvas(value || '')
  }, [drawImageToCanvas, value])

  const handlePointerDown = (event) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.setPointerCapture(event.pointerId)
    const point = getCanvasPoint(canvas, event.clientX, event.clientY)
    ctx.beginPath()
    ctx.moveTo(point.x, point.y)
    ctx.arc(point.x, point.y, 0.5, 0, Math.PI * 2)
    ctx.fillStyle = '#1d1d1d'
    ctx.fill()
    hasInkRef.current = true
    isDrawingRef.current = true
  }

  const handlePointerMove = (event) => {
    if (!isDrawingRef.current) return
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const point = getCanvasPoint(canvas, event.clientX, event.clientY)
    ctx.lineTo(point.x, point.y)
    ctx.stroke()
    hasInkRef.current = true
  }

  const finishDrawing = () => {
    if (!isDrawingRef.current) return
    isDrawingRef.current = false
    commitSignature()
  }

  const handleClear = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)
    signatureImageRef.current = ''
    hasInkRef.current = false
    onChange('')
    if (onSignedAtChange) {
      onSignedAtChange('')
    }
  }

  return (
    <div>
      <Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
        {subtitle}
      </Typography>
      <canvas
        ref={canvasRef}
        style={padStyle}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishDrawing}
        onPointerLeave={finishDrawing}
        onPointerCancel={finishDrawing}
      />
      <div style={{ marginTop: 8 }}>
        <Button variant="outlined" onClick={handleClear} size="small">
          Clear signature
        </Button>
      </div>
    </div>
  )
}

export default SignaturePad
